import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { CreateDatasetDialog } from "@/components/CreateDatasetDialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database as DatabaseIcon, Globe, User } from "lucide-react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";
import { allDatasetsLoader } from "@/routes/allDatasetsLoader";

const AllDatasets = () => {
	const navigate = useNavigate();
	const user = useAuthenticatedUser();
	const { datasets, projects, codes } =
		useLoaderData<typeof allDatasetsLoader>();
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [createDatasetDialogOpen, setCreateDatasetDialogOpen] = useState(false);
	const {
		createProject,
		createCode,
		createDataset,
		updateProjectTitle,
		updateCodeTitle,
	} = useWorkspaceSidebarActions();

	const handleCreateDataset = (
		name: string,
		datasetKind: "behavior_examples" | "operational_rules",
	) => {
		createDataset({ name, dataset_kind: datasetKind });
	};

	const handleDatasetClick = (datasetId: string) => {
		navigate(`/dataset/${datasetId}`);
	};

	return (
		<SidebarProvider>
			<div className="min-h-screen flex w-full">
				<WorkspaceSidebar
					projects={projects}
					codes={codes}
					datasets={datasets}
					onNewProject={createProject}
					onNewCode={createCode}
					onNewDataset={() => setCreateDatasetDialogOpen(true)}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1 overflow-auto">
					<div className="container mx-auto py-8 px-4">
						<div className="mb-6 flex items-start justify-between">
							<div>
								<h1 className="text-4xl font-bold text-foreground">
									All Datasets
								</h1>
								<p className="text-muted-foreground mt-2">
									Browse and select from all your test datasets
								</p>
							</div>
							<Button
								onClick={() => setCreateDatasetDialogOpen(true)}
								size="lg"
							>
								<DatabaseIcon className="mr-2 h-4 w-4" />
								Create New
							</Button>
						</div>

						{datasets.length === 0 ? (
							<Card>
								<CardContent className="py-12 text-center">
									<DatabaseIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
									<p className="text-muted-foreground">No datasets found</p>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{datasets.filter((d) => d.type === "primary").map((dataset) => (
									<Card
										key={dataset.id}
										className="cursor-pointer hover:border-primary transition-colors"
										data-testid={`dataset-card-${dataset.id}`}
										onClick={() => handleDatasetClick(dataset.id)}
									>
										<CardHeader>
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-2">
													<DatabaseIcon className="h-5 w-5 text-primary" />
													<CardTitle className="text-lg">
														{dataset.name}
													</CardTitle>
												</div>
												<div className="flex items-center gap-1">
													{dataset.is_public ? (
														<div title="Public dataset">
															<Globe className="h-4 w-4 text-muted-foreground" />
														</div>
													) : (
														<div title="Private dataset">
															<User className="h-4 w-4 text-muted-foreground" />
														</div>
													)}
												</div>
											</div>
											<CardDescription className="text-xs mt-2 flex items-center gap-2">
												<span>
													{dataset.user_id === user?.id
														? "Your dataset"
														: "Public dataset"}{" "}
													• Created{" "}
													{new Date(dataset.created_at).toLocaleDateString()}
												</span>
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-2">
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Type:
													</span>
													<p className="text-sm capitalize">{dataset.type}</p>
												</div>
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Dataset Kind:
													</span>
													<div className="mt-1">
														<Badge
															variant={
																dataset.dataset_kind === "behavior_examples"
																	? "default"
																	: "secondary"
															}
														>
															{dataset.dataset_kind === "behavior_examples"
																? "Behaviors"
																: "Rules"}
														</Badge>
													</div>
												</div>
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Last Updated:
													</span>
													<p className="text-sm">
														{new Date(dataset.updated_at).toLocaleDateString()}
													</p>
												</div>
											{dataset.projects && dataset.projects.length > 0 && (
													<div>
														<span className="text-xs font-semibold text-muted-foreground">
															Used in Projects:
														</span>
														<div className="mt-1 flex flex-wrap gap-1">
															{dataset.projects.map((project) => (
																<span
																	key={project.id}
																	className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
																>
																	{project.title}
																</span>
															))}
														</div>
													</div>
												)}
												{dataset.type === "primary" && (
													<div>
														<span className="text-xs font-semibold text-muted-foreground">
															Adversarial Datasets: ({dataset.derivatives?.length ?? 0})
														</span>
														<div className="mt-1">
															{dataset.derivatives && dataset.derivatives.length > 0 ? (
																<div className="flex flex-wrap gap-x-2 gap-y-1">
																	{(dataset.derivatives.length > 10
																		? dataset.derivatives.slice(0, 5)
																		: dataset.derivatives
																	).map((derivative) => (
																		<a
																			key={derivative.id}
																			href={`/dataset/${derivative.id}`}
																			className="text-xs text-primary hover:underline"
																			onClick={(e) => {
																				e.stopPropagation();
																				e.preventDefault();
																				navigate(`/dataset/${derivative.id}`);
																			}}
																		>
																			{derivative.name}
																		</a>
																	))}
																	{dataset.derivatives.length > 10 && (
																		<span className="text-xs text-muted-foreground">...</span>
																	)}
																</div>
															) : (
																<p className="text-sm text-muted-foreground">
																	No adversarial data sets.
																</p>
															)}
														</div>
													</div>
												)}
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</div>
				</main>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
				<CreateDatasetDialog
					open={createDatasetDialogOpen}
					onOpenChange={setCreateDatasetDialogOpen}
					onSubmit={handleCreateDataset}
				/>
			</div>
		</SidebarProvider>
	);
};

export default AllDatasets;
